import { type App, type Component, type Reactive } from "vue"
import { createVueApp } from "~/features/vue"
import type { ComponentEmits, ComponentProps, V } from "./types"
import VSimpleDialog from "@library/dialogs/VSimpleDialog.vue"
import VContextMenu from "@library/dialogs/VContextMenu.vue"
import VInput from '@library/inputs/VInput.vue'
import VNumber from '@library/inputs/VNumber.vue'
import VEmail from '@library/inputs/VEmail.vue'
import VPassword from '@library/inputs/VPassword.vue'
import { stopAndPrevent } from '@utils/events'
import type VTextArea from "@library/inputs/VTextArea.vue"
import type VSelect from "@library/inputs/VSelect.vue"
import type VCheckbox from "@library/inputs/VCheckbox.vue"
import type VSmartInput from "@library/inputs/VSmartInput.vue"

type OnCloseEmitParameters<C extends Component> = Parameters<NonNullable<ComponentEmits<C>['onClose']>>

const dialogQueue = (() => {
  let promise = Promise.resolve()

  return {
    enqueue: (fn: () => Promise<void>) => {
      promise = promise.then(fn)
    }
  }
})()

type DialogInstance = {
  _closed?: boolean
  _app?: App<Element>
  _element?: Element
  _resolve?: () => void

  setup: () => Promise<void>
  close: (resolve?: boolean) => void
}

/**
 * Set main and any currently present dialogs as inert 
 */
function setCoveredElementsAsInert () {
  const inertableElements = [
    ...document.querySelectorAll('main'),
    ...document.querySelectorAll('.v-dialog-container')
  ]

  for (const element of inertableElements) {
    element.setAttribute('inert', 'true')
  }
}

/**
 * Remove inert attribute from last inert main or dialog
 */
function unsetCoveredElementsAsInert () {
  const uninertableElements = [
    ...document.querySelectorAll('main[inert]'),
    ...document.querySelectorAll('.v-dialog-container[inert]')
  ]

  uninertableElements.at(-1)?.removeAttribute('inert')
}

function createDialogInstance<C extends Component, CProps extends ComponentProps<C>, CloseCallback extends (...params: OnCloseEmitParameters<C>) => void>(
  component: C,
  props: CProps,
  options: {
    callback?: CloseCallback
    immediate?: boolean
    injects?: Record<string, unknown>
  }
): DialogInstance {
  const state: DialogInstance = {
    _closed: false,
    setup () {
      return new Promise<void>((resolve) => {
        if (state._closed) return resolve()

        state._resolve = resolve
        const element = document.createElement('div')

        // TODO: Temporary workaround for weirdness in the app. Remove when not necessary
        element.setAttribute('class', 'relative z-[1000] v-dialog-container')

        setCoveredElementsAsInert()

        state._element = element
        state._app = createVueApp(component, Object.assign(props, {
          onClose: (...params: Parameters<CloseCallback>) => {
            state.close(false)

            options.callback?.(...params)

            resolve()
          }
        }))
        
        if (options.injects) {
          Object.entries(options.injects).forEach(([key, value]) => {
            state._app!.provide(key, value)
          })
        }
    
        state._app.mount(state._element)
      
        document.body.appendChild(state._element)
      })
    },
    close (resolve = true) {
      this._app?.unmount()
      this._element?.remove()

      this._closed = true

      unsetCoveredElementsAsInert()

      if (resolve) {
        this._resolve?.()
      }
    }
  }

  return state
}

/**
 * Function that queues and displays dialogs on the page
 *
 * @template C - The type of the Vue component
 * @template CProps - The type of the props for the Vue component
 * @template CloseCallback - The type of the callback function for the dialog close event
 *
 * @param {C} component - A Vue 3 component that uses VDialog component and implements the 'close' emit
 * @param {CProps} [props=Object.create(null)] - Props that will be forwarded to the dialog component when mounted
 * @param {Object} [options={}] - Options object that modify the behavior or the consequences of the dialog
 * @param {CloseCallback} [options.callback] - A callback function that will be called when the dialog closes
 * @param {boolean} [options.immediate=false] - If true, the dialog is displayed immediately
 */
export function useDialog<C extends Component, CProps extends ComponentProps<C>, CloseCallback extends (...params: OnCloseEmitParameters<C>) => void>(
  component: C,
  props: CProps,
  options: {
    callback?: CloseCallback
    immediate?: boolean
    injects?: Record<string, unknown>
  } = {}
) {
  const state = createDialogInstance(component, props, options)

  if (options.immediate) {
    void state.setup()
  } else {
    dialogQueue.enqueue(() => state.setup())
  }

  return state as { close: () => void }
}

export function useSimpleDialog<
  CProps extends Omit<ComponentProps<typeof VSimpleDialog>, 'callbacks'>
>(
  /**
   * Dialog props
   */
  props: CProps,
  options: {
    /**
     * Method called when dialog is about to close. Supports async with integrated `useSubmit` functionality.
     */
    callback?: (decision: boolean) => void | Promise<void>
    /**
     * Method called when user takes positive action. Supports async with integrated `useSubmit` functionality.
     */
    onAccept?: () => void | Promise<void>
    /**
     * Method called when user takes negative action. Supports async with integrated `useSubmit` functionality.
     */
    onReject?: () => void | Promise<void>
    immediate?: boolean
    injects?: Record<string, unknown>
  } = {}
) {
  return useDialog(
    VSimpleDialog,
    {
      ...props,
      callbacks: {
        any: options.callback,
        accept: options.onAccept,
        reject: options.onReject
      }
    },
    {
      immediate: options.immediate,
      injects: options.injects
    }
  )
}

export function useContextMenu<T> (event: MouseEvent, items: Reactive<V.ContextMenu.Item<T>[]>, context: T) {
  return new Promise<void>((resolve) => {
    const element = document.createElement('div')

    const instance = createVueApp(VContextMenu as Component, {
      items,
      context,
      position: {
        left: event.x,
        top: event.y
      },
      onClose: () => {
        instance.unmount()
        element.remove()

        resolve()
      }
    })
    
    instance.mount(element)
  
    document.body.appendChild(element)
  })
}

type UseFilePickerProps = {
  accept: string | string[]
  callback: (...files: File[]) => void
  // Optionals
  multiple?: boolean
}

export function useFilePicker (props: UseFilePickerProps) {
  const input = document.createElement('input')
  input.type = 'file'
  input.multiple = props.multiple || false
  input.accept = typeof props.accept === 'string' ? props.accept : props.accept.join(',')
  input.setAttribute('class', 'hidden')

  document.body.appendChild(input)

  input.addEventListener('change', (event) => {
    stopAndPrevent(event)
  
    const target = event.target as HTMLInputElement
    const files = Array.from(target.files as FileList)
  
    props.callback(...files)
  }, { once: true })

  input.click()
  input.remove()
}

export declare const DialogFieldMetadata: unique symbol;

type DialogFieldType<TFieldType, TReturnType, TComponent extends Component> = {
  type: TFieldType
  default?: TReturnType | null
} & Omit<
  ComponentProps<TComponent>, 'type' | 'default' | 'modelValue'
> & {
  [DialogFieldMetadata]?: {
    returnType: TReturnType
    componentType: TComponent
  }
}

export type DialogField = |
  DialogFieldType<'text', string, typeof VInput> |
  DialogFieldType<'smarttext', string, typeof VSmartInput> |
  DialogFieldType<'textarea', string, typeof VTextArea> |
  DialogFieldType<'number', number, typeof VNumber> |
  DialogFieldType<'email', string, typeof VEmail> |
  DialogFieldType<'password', string, typeof VPassword> |
  DialogFieldType<'select', string, typeof VSelect> |
  DialogFieldType<'checkbox', boolean, typeof VCheckbox>

export type DialogFieldsReturnType<T extends DialogField[]> = {
  [K in keyof T]: (
    T[K] extends { type: 'number' } ? (
      number
    ) : (
      T[K] extends { type: 'checkbox' } ? (
        boolean
      ) : (
        string
      )
    )
  )
}

export type DialogFieldComponentType<T extends DialogField> = Exclude<T[typeof DialogFieldMetadata], undefined>['componentType']
export type DialogFieldReturnType<T extends DialogField> = Exclude<T[typeof DialogFieldMetadata], undefined>['returnType']

export function usePrompt<
  CField extends DialogField,
  const CFields extends CField[],
  CProps extends Omit<ComponentProps<typeof VSimpleDialog>, 'callbacks' | 'fields'>
>(
  props: CProps & { fields: CFields },
  options: {
    /**
     * Method called when dialog is about to close. Supports async with integrated `useSubmit` functionality.
     */
    callback?: (decision: boolean, ...params: DialogFieldsReturnType<CFields>) => void | Promise<void>
    /**
     * Method called when user takes positive action. Supports async with integrated `useSubmit` functionality.
     */
    onAccept?: (...params: DialogFieldsReturnType<CFields>) => void | Promise<void>
    /**
     * Method called when user takes negative action. Supports async with integrated `useSubmit` functionality.
     */
    onReject?: () => void | Promise<void>
    injects?: Record<string, unknown>
  } = {}
) {
  return new Promise<[boolean, ...DialogFieldsReturnType<CFields>]>((resolve) => {
    useDialog(
      VSimpleDialog,
      {
        ...props,
        buttons: props.buttons || ['cancel', 'ok'] as const,
        callbacks: {
          any: options.callback as undefined | ((decision: boolean, ...values: Array<string | number | boolean | undefined | null>) => void | Promise<void>),
          accept: options.onAccept as undefined | ((...values: Array<string | number | boolean | undefined | null>) => void | Promise<void>),
          reject: options.onReject
        }
      },
      {
        injects: options.injects,
        immediate: true,
        callback: (...params) => {
          resolve(params as [boolean, ...DialogFieldsReturnType<CFields>])
        }
      }
    )
  })
}